// @ts-check
import { useCallback, useEffect, useRef, useState } from 'react';
import adminDashboardService from '../services/adminDashboardService';

/** Dashboard blocks for the chosen range. Only the latest request may update state, so a slow earlier one never overwrites it. */
export default function useDashboardSummary() {
  const [range, setRange] = useState(/** @type {'7d' | '30d'} */ ('7d'));
  const [blocks, setBlocks] = useState(/** @type {Record<string, any>} */ ({}));
  const [failed, setFailed] = useState(/** @type {string[]} */ ([]));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(/** @type {Error | null} */ (null));
  const [generatedAt, setGeneratedAt] = useState(/** @type {string | null} */ (null));
  const requestId = useRef(0);

  const load = useCallback(
    async (fresh = false) => {
      const id = ++requestId.current;
      setLoading(true);
      try {
        // First load and range switches may be served from the server's 60s cache; Refresh asks for fresh numbers.
        const data = fresh
          ? await adminDashboardService.getSummary(range, { fresh: true })
          : await adminDashboardService.getSummary(range);
        if (id !== requestId.current) return;
        setBlocks(data?.blocks || {});
        setFailed(data?.failed || []);
        setGeneratedAt(data?.generated_at || null);
        setError(null);
      } catch (err) {
        if (id !== requestId.current) return;
        // Drop old numbers so they never sit under a new range label.
        setBlocks({});
        setFailed([]);
        setGeneratedAt(null);
        setError(err instanceof Error ? err : new Error('Failed to load dashboard'));
      } finally {
        if (id === requestId.current) setLoading(false);
      }
    },
    [range],
  );

  useEffect(() => {
    load();
  }, [load]);

  // Ignores its argument, so it can be passed straight to onClick.
  const refresh = useCallback(() => load(true), [load]);

  return { blocks, failed, loading, error, range, setRange, refresh, generatedAt };
}
